import { computed, onUnmounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { KitchenMasterStateModel } from '@/model/kitchen';
import {
  describeMasterLocation,
  fetchKitchenMaster,
  isMasterOnline,
  isSameMaster,
  saveKitchenMaster,
} from '@/service/kitchenMaster';

const uniqueId = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (value) =>
    value.toString(16).padStart(2, '0'),
  ).join('');
const getDeviceId = (): string => {
  const created = uniqueId();
  try {
    const saved = localStorage.getItem('chef_screen_device_id');
    if (saved) return saved;
    localStorage.setItem('chef_screen_device_id', created);
  } catch (error) {
    console.warn('保存厨师大屏设备标识失败，本次使用临时标识', error);
  }
  return created;
};

export const useKitchenMaster = (
  shopId: Ref<string>,
  kitchenId: Ref<string>,
  now: Ref<number>,
  identity: () => { account: string; accountName: string },
  onDemoted: (hasOtherMasterOnDevice: boolean) => void,
) => {
  const deviceId = getDeviceId();
  let sessionId = uniqueId();
  const isReload = performance
    .getEntriesByType('navigation')
    .some((entry) => 'type' in entry && entry.type === 'reload');
  let restoreLegacySession = false;
  const sessionKey = (shop = shopId.value, kitchen = kitchenId.value): string =>
    `chef_screen_session_${JSON.stringify([identity().account, shop, kitchen])}`;
  const persistSession = (key = sessionKey(), value = sessionId): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn('保存厨师大屏页面标识失败', error);
    }
  };
  const loadSession = (): void => {
    sessionId = uniqueId();
    restoreLegacySession = false;
    if (!shopId.value || !kitchenId.value || kitchenId.value === 'all') return;
    try {
      // 刷新复用当前标签页标识；新打开或复制的标签页生成新标识。
      const saved = isReload ? sessionStorage.getItem(sessionKey()) : null;
      if (saved) sessionId = saved;
      else restoreLegacySession = isReload;
    } catch (error) {
      console.warn('读取厨师大屏页面标识失败', error);
      restoreLegacySession = isReload;
    }
    persistSession();
  };
  loadSession();
  let version = 0;
  let disposed = false;
  const active = ref(false);
  const verified = ref(false);
  const masterBusy = ref(false);
  const masterState = ref<KitchenMasterStateModel | null>(null);
  const masterMessage = ref('');
  const masterError = ref('');
  const ownState = (): KitchenMasterStateModel => ({
    ...identity(),
    deviceId,
    sessionId,
    enabled: true,
    heartbeatAt: Date.now(),
  });
  const isMaster = computed(
    () => active.value && verified.value && isMasterOnline(masterState.value, now.value),
  );
  const demote = (state: KitchenMasterStateModel | null): void => {
    active.value = false;
    verified.value = false;
    onDemoted(
      Boolean(state?.enabled && state.deviceId === deviceId && !isSameMaster(state, ownState())),
    );
    masterMessage.value =
      state?.enabled && !isSameMaster(state, ownState())
        ? `主屏已被${describeMasterLocation(state, ownState())}接管，当前页面已停止自动分配。`
        : '当前页面已停止自动分配，如需继续，请重新开启主屏。';
  };
  const pauseMaster = (): void => {
    verified.value = false;
  };
  const syncMaster = async (): Promise<boolean> => {
    if (!shopId.value || !kitchenId.value || kitchenId.value === 'all' || disposed) return false;
    const currentVersion = version;
    const shop = Number(shopId.value);
    const kitchen = Number(kitchenId.value);
    const expected = ownState();
    try {
      let state = await fetchKitchenMaster(kitchen, shop);
      if (currentVersion !== version || disposed) return false;
      masterState.value = state;
      masterError.value = '';
      if (!active.value) return false;
      if (!isSameMaster(state, expected) || !isMasterOnline(state)) {
        demote(state);
        return false;
      }
      // 每轮先查归属；长队列逐条分配时也检查，心跳最多每 15 秒更新一次。
      if (Date.now() - (state?.heartbeatAt || 0) >= 15000) {
        state = await saveKitchenMaster(kitchen, shop, expected, 'heartbeat');
        if (currentVersion !== version || disposed) return false;
        masterState.value = state;
        if (!isSameMaster(state, expected) || !isMasterOnline(state)) {
          demote(state);
          return false;
        }
      }
      verified.value = true;
      masterMessage.value = '';
      return true;
    } catch (error) {
      if (currentVersion !== version || disposed) return false;
      console.error('检查厨房主屏失败', error);
      verified.value = false;
      masterError.value =
        error instanceof Error ? error.message : '暂时无法连接自动分配主屏，请检查网络后刷新';
      return false;
    }
  };
  const claimMaster = async (force = false): Promise<boolean> => {
    if (masterBusy.value || disposed) return false;
    const currentVersion = version;
    masterBusy.value = true;
    try {
      if (!force && restoreLegacySession) {
        const previous = await fetchKitchenMaster(Number(kitchenId.value), Number(shopId.value));
        if (currentVersion !== version || disposed) return false;
        // 兼容旧版本未保存页面标识的主屏，仅在浏览器刷新且账号、设备均一致时恢复。
        if (
          previous?.enabled &&
          previous.account === identity().account &&
          previous.deviceId === deviceId
        ) {
          sessionId = previous.sessionId;
          persistSession();
        }
      }
      restoreLegacySession = false;
      const expected = ownState();
      const state = await saveKitchenMaster(
        Number(kitchenId.value),
        Number(shopId.value),
        { ...expected, takeoverAt: force ? Date.now() : undefined },
        force ? 'force' : 'acquire',
      );
      if (currentVersion !== version || disposed) return false;
      masterState.value = state;
      masterError.value = '';
      active.value = isSameMaster(state, expected) && isMasterOnline(state);
      verified.value = active.value;
      if (!active.value) demote(state);
      else masterMessage.value = '';
      return active.value;
    } catch (error) {
      if (currentVersion !== version || disposed) return false;
      console.error('开启厨房主屏失败', error);
      active.value = false;
      verified.value = false;
      masterError.value = error instanceof Error ? error.message : '开启主屏失败，请重试';
      return false;
    } finally {
      if (currentVersion === version && !disposed) masterBusy.value = false;
    }
  };
  const release = async (
    shop: string,
    kitchen: string,
    expected: KitchenMasterStateModel,
  ): Promise<void> => {
    if (!shop || !kitchen || kitchen === 'all') return;
    try {
      await saveKitchenMaster(
        Number(kitchen),
        Number(shop),
        { ...expected, enabled: false },
        'release',
      );
    } catch (error) {
      console.warn('关闭厨房主屏失败，将等待心跳超时', error);
    }
  };
  const stopMaster = async (): Promise<void> => {
    const expected = ownState();
    active.value = false;
    verified.value = false;
    version++;
    sessionId = uniqueId();
    restoreLegacySession = false;
    persistSession();
    masterBusy.value = true;
    try {
      await release(shopId.value, kitchenId.value, expected);
      await syncMaster();
    } finally {
      masterBusy.value = false;
    }
  };
  watch(
    [shopId, kitchenId],
    (value, previous) => {
      const expected = ownState();
      const wasActive = active.value;
      version++;
      if (wasActive) persistSession(sessionKey(previous[0], previous[1]), uniqueId());
      loadSession();
      active.value = false;
      verified.value = false;
      masterBusy.value = false;
      masterState.value = null;
      masterMessage.value = '';
      masterError.value = '';
      if (wasActive) void release(previous[0], previous[1], expected);
    },
    { flush: 'sync' },
  );
  const masterNotice = computed(() => {
    if (masterError.value) return masterError.value;
    if (masterMessage.value) return masterMessage.value;
    const state = masterState.value;
    if (state && isMasterOnline(state, now.value)) {
      if (isMaster.value) return '';
      if (isSameMaster(state, ownState())) {
        return '暂时无法确认当前主屏是否正在运行，请刷新查看。';
      }
      return `自动分配主屏正在${describeMasterLocation(
        state,
        ownState(),
      )}运行，当前页面会自动显示分配好的菜品。`;
    }
    return masterState.value
      ? '自动分配主屏已关闭或断开连接，请检查原页面，或在这里开启主屏。'
      : '自动分配主屏尚未开启，请在一台设备上开启主屏。';
  });
  onUnmounted(() => {
    disposed = true;
    version++;
    if (active.value) {
      // 主动离开页面后更换标识，避免延迟关闭请求影响随后重新开启的主屏。
      persistSession(sessionKey(), uniqueId());
      void release(shopId.value, kitchenId.value, ownState());
    }
  });
  return {
    isMaster,
    masterSelected: active,
    masterBusy,
    masterNotice,
    claimMaster,
    stopMaster,
    syncMaster,
    pauseMaster,
  };
};
